import { Loader } from '../data-table/components/loader/loader';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useUnsavedChangesWarning } from '../../services/hooks';
import { WorkerStore } from '../../stores/worker-configuration/worker.store';
import { ConfigurationAutomationDetail } from './detail/configuration-automation-detail';
import { ConfigurationCatalogBanner } from './detail/configuration-catalog-banner';
import { ConfigurationDetail } from './detail/configuration-detail';
import { ConfigurationSidebar } from './sidebar/configuration-sidebar';
import * as S from './configuration-page.styles';

export interface IConfigurationPageContentProps {
    store: WorkerStore;
}

export const ConfigurationPageContent: React.FC<IConfigurationPageContentProps> = observer(({ store }) => {
    useEffect(() => {
        store.fetchConfiguration();
    }, [store]);

    // `dirty` is also true for the option defaults the store seeds into the draft at load
    // (see initializeDraftFromConfiguration) — a real pending diff, but one the machine made,
    // re-created on every visit until someone saves. Warning about it on the way out reads as
    // "you have unsaved changes" to an operator who changed nothing. The banner and the
    // Save/Discard actions still show it; only the navigation + beforeunload prompt is narrowed,
    // and it returns the moment a genuine edit makes this false.
    useUnsavedChangesWarning(!store.readOnly && store.dirty && !store.hasRecommendedDefaultUpdatesOnly);

    const interactionDisabled = store.readOnly || store.saving;
    const showCatalogBanner = !store.readOnly && store.catalogImporting;
    const automationValidationError = store.automationRuleValidationError;
    const showSaveActions = !store.readOnly && store.dirty;
    const showMeta =
        !store.loading &&
        !store.error &&
        store.configuration &&
        (showCatalogBanner ||
            showSaveActions ||
            (!store.readOnly && Boolean(store.saveError || automationValidationError)));

    return (
        <S.Wrapper data-readonly={store.readOnly ? 'true' : undefined}>
            <S.PageHeader>
                <S.PageHeaderMain>
                    <S.Title>Worker Configuration</S.Title>
                </S.PageHeaderMain>

                {showMeta && (
                    <S.Meta>
                        {showCatalogBanner && <ConfigurationCatalogBanner store={store} />}
                        {showSaveActions && (
                            <S.UnsavedLabel>
                                {store.hasRecommendedDefaultUpdatesOnly
                                    ? 'Update config with recommended defaults'
                                    : 'Unsaved changes'}
                            </S.UnsavedLabel>
                        )}
                        {!store.readOnly && (store.saveError || automationValidationError) && (
                            <S.SaveError>{store.saveError || automationValidationError}</S.SaveError>
                        )}
                        {showSaveActions && (
                            <>
                                <S.ActionButton
                                    type="button"
                                    onClick={() => store.discardChanges()}
                                    disabled={store.saving}
                                >
                                    Discard
                                </S.ActionButton>
                                <S.PrimaryButton
                                    type="button"
                                    onClick={() => void store.saveConfiguration()}
                                    disabled={store.saving || Boolean(automationValidationError)}
                                >
                                    {store.saving ? 'Saving…' : 'Save changes'}
                                </S.PrimaryButton>
                            </>
                        )}
                    </S.Meta>
                )}
            </S.PageHeader>

            {store.loading && (
                <S.LoadingContainer>
                    <Loader loading={true} />
                </S.LoadingContainer>
            )}

            {store.error && <S.ErrorContainer>Error: {store.error}</S.ErrorContainer>}

            {!store.loading && !store.error && (
                <S.Content>
                    <ConfigurationSidebar store={store} />
                    <S.ReadOnlyDetailShell data-readonly={store.readOnly ? 'true' : undefined}>
                        {store.isAutomationWorkerSelected ? (
                            <ConfigurationAutomationDetail
                                store={store}
                                interactionDisabled={interactionDisabled}
                            />
                        ) : (
                            <ConfigurationDetail store={store} interactionDisabled={interactionDisabled} />
                        )}
                    </S.ReadOnlyDetailShell>
                </S.Content>
            )}
        </S.Wrapper>
    );
});
